import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/company/companyAdd.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCompany, createCompanyWarehouse, warehouseList, getCountries, getStates, getCities } from '../../services/Api';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import PopUpMessage from '../../utils/errors/PopUpMessage';
import { AiOutlineLoading } from 'react-icons/ai';
import OpenEye from '../../assets/images/Auth/visibility.png';
import CloseEye from '../../assets/images/Auth/visibility_off.png';

const CompanyAdd = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('company');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [companyId, setCompanyId] = useState(null);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isCompanyValidated, setIsCompanyValidated] = useState(false);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState({
        countries: false,
        states: false,
        cities: false
    });
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseData, setWarehouseData] = useState([{
        warehouse_id: '',
        membership_till: '',
        lot_min: '25',
        lot_max: '',
        is_primary: true
    }]);

    // Company form data
    const [companyData, setCompanyData] = useState({
        name: '',
        uen_number: '',
        contact_number: '',
        email: '',
        address: '',
        country_id: '',
        country_code: '',
        state_id: '',
        city_id: '',
        password: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        remark: '',
        contact_person_phone: ''
    });

    // Validation rules - SAME PATTERN AS WAREHOUSE EDIT
    const validationRules = {
        name: {
            required: true,
            message: 'Please enter a company name'
        },
        uen_number: {
            required: true,
            message: 'Please enter a company UEN number'
        },
        contact_number: {
            required: true,
            pattern: /^[6-9]\d{9}$/,
            message: 'Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid email address'
        },
        address: {
            required: true,
            message: 'Please enter a company address'
        },
        country_id: {
            required: true,
            message: 'Please select a country'
        },
        state_id: {
            required: true,
            message: 'Please select a state',
            validate: (value) => {
                // Allow empty or "no-states" option
                return value !== '';
            }
        },
        city_id: {
            required: true,
            message: 'Please select a city',
            validate: (value) => {
                // Allow empty or "no-cities" option
                return value !== '';
            }
        },
        password: {
            required: true,
            pattern: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
            message: 'Password must contain at least 8 characters, one uppercase letter, one number, and one special character'
        },
        contact_name: {
            required: true,
            message: 'Please enter a contact person name'
        },
        contact_email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid contact email address'
        },
        contact_phone: {
            required: true,
            pattern: /^[6-9]\d{9}$/,
            message: 'Please enter a valid 10-digit contact phone number starting with 6, 7, 8, or 9'
        }
    };

    const validationRulesWarehouse = {
        warehouse_id: {
            required: true,
            message: 'Please select a warehouse location'
        },
        membership_till: {
            required: true,
            message: 'Please select a membership till date'
        },
        lot_min: {
            required: true,
            message: 'Please enter a minimum lot number'
        },
        lot_max: {
            required: true,
            message: 'Please enter a maximum lot number'
        }
    };

    const handleTabChange = (tab) => {
        // if (tab === 'warehouse') {
        //     if (!validateCompanyForm()) {
        //         showPopup('info', 'Alert', 'Please complete company details first');
        //         return;
        //     }
        //     setIsCompanyValidated(true);
        // }
        setActiveTab(tab);
    };

    useEffect(() => {
        console.log('CompanyId state changed:', companyId);
    }, [companyId]);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseList();
                setWarehouses(response.data);
            } catch (error) {
                console.error("Error fetching warehouses:", error);
                showPopup('error', 'Error', 'Failed to load warehouses');
            }
        };

        const fetchCountries = async () => {
            setLoadingLocations(prev => ({ ...prev, countries: true }));
            try {
                const response = await getCountries();
                if (response.data && Array.isArray(response.data.data)) {
                    setCountries(response.data.data);
                } else {
                    console.error("Expected an array for countries, but got:", response.data);
                    showPopup('error', 'Error', 'Failed to load countries: Invalid data format');
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
                showPopup('error', 'Error', 'Failed to load countries');
            } finally {
                setLoadingLocations(prev => ({ ...prev, countries: false }));
            }
        };

        fetchWarehouses();
        fetchCountries();

        if (location.state?.barcode) {
            setCompanyData(prev => ({
                ...prev,
                barcode_number: location.state.barcode
            }));
        }
    }, [location.state]);

    // Fetch states when country changes
    useEffect(() => {
        const fetchStates = async () => {
            if (!companyData.country_code) {
                setStates([]);
                return;
            }
            setLoadingLocations(prev => ({ ...prev, states: true }));
            try {
                // Use country_code to fetch states
                const response = await getStates(companyData.country_code);
                if (response.data && Array.isArray(response.data.data)) {
                    const statesData = response.data.data;

                    // If statesData is empty, set a special "no states" option
                    if (statesData.length === 0) {
                        setStates([{
                            id: 'no-states',
                            name: 'No states available',
                            disabled: true
                        }]);
                    } else {
                        setStates(statesData);
                    }
                } else {
                    console.error("Expected an array for states, but got:", response.data);
                    showPopup('error', 'Error', 'Failed to load states: Invalid data format');
                    setStates([{
                        id: 'no-states',
                        name: 'No states available',
                        disabled: true
                    }]);
                }
                setCompanyData(prev => ({
                    ...prev,
                    state_id: '',
                    city_id: ''
                }));
                setCities([]);
            } catch (error) {
                console.error("Error fetching states:", error);
                showPopup('error', 'Error', 'Failed to load states');
                setStates([{
                    id: 'no-states',
                    name: 'No states available',
                    disabled: true
                }]);
            } finally {
                setLoadingLocations(prev => ({ ...prev, states: false }));
            }
        };

        fetchStates();
    }, [companyData.country_code]);

    // Fetch cities when state changes
    useEffect(() => {
        const fetchCities = async () => {
            if (!companyData.state_id) {
                setCities([]);
                return;
            }
            setLoadingLocations(prev => ({ ...prev, cities: true }));
            try {
                const response = await getCities(companyData.state_id);
                if (response.data && Array.isArray(response.data.data)) {
                    const citiesData = response.data.data;

                    // If citiesData is empty, set a special "no cities" option
                    if (citiesData.length === 0) {
                        setCities([{
                            id: 'no-cities',
                            name: 'No cities available',
                            disabled: true
                        }]);
                    } else {
                        setCities(citiesData);
                    }
                } else {
                    console.error("Expected an array for cities, but got:", response.data);
                    showPopup('error', 'Error', 'Failed to load cities: Invalid data format');
                    setCities([{
                        id: 'no-cities',
                        name: 'No cities available',
                        disabled: true
                    }]);
                }
                setCompanyData(prev => ({
                    ...prev,
                    city_id: ''
                }));
            } catch (error) {
                console.error("Error fetching cities:", error);
                showPopup('error', 'Error', 'Failed to load cities');
                setCities([{
                    id: 'no-cities',
                    name: 'No cities available',
                    disabled: true
                }]);
            } finally {
                setLoadingLocations(prev => ({ ...prev, cities: false }));
            }
        };

        fetchCities();
    }, [companyData.state_id]);

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    // VALIDATE COMPANY FORM - SAME PATTERN AS WAREHOUSE EDIT
    const validateCompanyForm = () => {
        const validationErrors = {};

        Object.keys(validationRules).forEach(field => {
            const rule = validationRules[field];
            const value = companyData[field];

            // Skip validation for state_id if it's "no-states" option
            if (field === 'state_id' && value === 'no-states') {
                return;
            }

            // Skip validation for city_id if it's "no-cities" option
            if (field === 'city_id' && value === 'no-cities') {
                return;
            }

            // Required field validation
            if (rule.required) {
                if (!value || value.toString().trim() === '') {
                    validationErrors[field] = rule.message;
                }
            }

            // Pattern validation
            if (rule.pattern && value) {
                if (!rule.pattern.test(value)) {
                    validationErrors[field] = rule.message;
                }
            }

            // Custom validation if exists
            if (rule.validate && !rule.validate(value)) {
                validationErrors[field] = rule.message;
            }
        });

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    // VALIDATE WAREHOUSE FORM - SAME PATTERN AS WAREHOUSE EDIT
    const validateWarehouseForm = () => {
        const validationErrors = {};

        warehouseData.forEach((warehouse, index) => {
            Object.keys(validationRulesWarehouse).forEach(field => {
                const rule = validationRulesWarehouse[field];
                const value = warehouse[field];

                // Required field validation
                if (rule.required) {
                    if (!value || value.toString().trim() === '') {
                        validationErrors[`${field}_${index}`] = rule.message;
                    }
                }
            });

            // Additional validation: max > min
            if (warehouse.lot_min && warehouse.lot_max) {
                const minValue = parseInt(warehouse.lot_min, 10);
                const maxValue = parseInt(warehouse.lot_max, 10);

                if (!isNaN(minValue) && !isNaN(maxValue) && maxValue < minValue) {
                    validationErrors[`lot_max_${index}`] = `Maximum lot must be greater than or equal to minimum (${minValue})`;
                }
            }
        });

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleCompanyInputChange = (e) => {
        const { name, value } = e.target;

        if (isCompanyValidated) {
            setIsCompanyValidated(false);
        }

        // Handle phone number fields - only allow digits and limit to 10 characters
        if (name === 'contact_number' || name === 'contact_phone') {
            const digitsOnly = value.replace(/\D/g, '');
            const limitedValue = digitsOnly.slice(0, 10);

            setCompanyData(prev => ({
                ...prev,
                [name]: limitedValue
            }));

            const selectElement = e.target;
            if (limitedValue) {
                selectElement.style.color = '#000000';
            } else {
                selectElement.style.color = '#797775';
            }

            // Clear error when user starts typing
            if (errors[name]) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }

            return;
        }

        // Handle country selection specially
        if (name === 'country_id') {
            // Find the selected country
            const selectedCountry = countries.find(country =>
                country.id == value ||
                country.country_code === value ||
                country.code === value
            );

            setCompanyData(prev => ({
                ...prev,
                country_id: selectedCountry?.id || '',      // Store numeric ID
                country_code: selectedCountry?.code || selectedCountry?.country_code || '', // Store code
                state_id: '',
                city_id: ''
            }));

            const selectElement = e.target;
            if (value) {
                selectElement.style.color = '#000000';
            } else {
                selectElement.style.color = '#797775';
            }

            // Clear error when user starts typing
            if (errors.country_id) {
                setErrors(prev => ({
                    ...prev,
                    country_id: ''
                }));
            }

            return;
        }

        // Handle other fields normally
        setCompanyData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'state_id' && { city_id: '' })
        }));

        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Real-time validation for specific fields
        if (name === 'email' && value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(value)) {
                setErrors(prev => ({ ...prev, email: "Please enter a valid email address" }));
            } else {
                setErrors(prev => ({ ...prev, email: "" }));
            }
        }

        if (name === 'contact_email' && value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(value)) {
                setErrors(prev => ({ ...prev, contact_email: "Please enter a valid contact email address" }));
            } else {
                setErrors(prev => ({ ...prev, contact_email: "" }));
            }
        }

        if (name === 'password' && value) {
            const passwordPattern = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
            if (!passwordPattern.test(value)) {
                setErrors(prev => ({ ...prev, password: "Password must contain at least 8 characters, one uppercase letter, one number, and one special character" }));
            } else {
                setErrors(prev => ({ ...prev, password: "" }));
            }
        }
    };

    const handleNext = async () => {
        // Validate form before allowing to go to warehouse tab
        if (!validateCompanyForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required fields correctly');
            return;
        }

        // Check if "no states" or "no cities" options are selected
        if (companyData.state_id === 'no-states' || companyData.city_id === 'no-cities') {
            showPopup('error', 'Validation Error', 'Selected location does not have states or cities available. Please select a different country.');
            return;
        }

        setIsCompanyValidated(true);
        setActiveTab('warehouse');
    };

    const handleWarehouseInputChange = (e, index) => {
        const { name, value } = e.target;
        setWarehouseData(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [name]: value
            };
            return updated;
        });

        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        // Clear specific field error when user starts typing
        const errorKey = `${name}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: ''
            }));
        }
    };

    const handleWarehouseNumberInput = (index, field, value) => {
        const numericValue = value.replace(/\D/g, '');

        if (field === 'lot_min') {
            return;
        }

        setWarehouseData(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: numericValue
            };
            return updated;
        });

        const errorKey = `${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: ''
            }));
        }

        // Validate max > min when max is being entered
        if (field === 'lot_max' && numericValue) {
            const minValue = parseInt(warehouseData[index].lot_min || '25', 10);
            const maxValue = parseInt(numericValue, 10);

            if (maxValue < minValue) {
                setErrors(prev => ({
                    ...prev,
                    [`lot_max_${index}`]: `Maximum lot must be greater than or equal to minimum (${minValue})`
                }));
            } else {
                // Clear max error if it exists
                if (errors[`lot_max_${index}`]) {
                    setErrors(prev => ({
                        ...prev,
                        [`lot_max_${index}`]: ''
                    }));
                }
            }
        }
    };

    useEffect(() => {
        // Maintain select field colors for company form
        const companySelects = document.querySelectorAll('[name="country_id"], [name="state_id"], [name="city_id"]');
        companySelects.forEach(select => {
            if (select.value) {
                select.style.color = '#000000';
            } else {
                select.style.color = '#797775';
            }
        });

        // Maintain select field colors for warehouse forms
        const warehouseSelects = document.querySelectorAll('[name="warehouse_id"]');
        warehouseSelects.forEach(select => {
            if (select.value) {
                select.style.color = '#000000';
            } else {
                select.style.color = '#c9c9c9';
            }
        });
    }, [activeTab, companyData, warehouseData]);

    const handlePrimaryChange = (index) => {
        const updatedWarehouses = warehouseData.map((warehouse, i) => ({
            ...warehouse,
            is_primary: i === index
        }));
        setWarehouseData(updatedWarehouses);
    };

    const addNewWarehouse = () => {
        setWarehouseData(prev => [
            ...prev,
            {
                warehouse_id: '',
                membership_till: '',
                lot_min: '',
                lot_max: '',
                is_primary: false
            }
        ]);
    };

    const removeWarehouse = (index) => {
        if (warehouseData.length === 1) return;

        setWarehouseData(prev => {
            const updated = prev.filter((_, i) => i !== index);
            if (prev[index].is_primary && updated.length > 0) {
                updated[0].is_primary = true;
            }
            return updated;
        });
    };

    const handleSubmitWarehouse = async (e) => {
        e.preventDefault();

        // STEP 1: Validate company form again (in case user changed data)
        if (!validateCompanyForm()) {
            // Find which fields are missing
            const missingFields = Object.keys(validationRules)
                .filter(field => {
                    const rule = validationRules[field];
                    const value = companyData[field];

                    // Skip special cases
                    if (field === 'state_id' && value === 'no-states') return false;
                    if (field === 'city_id' && value === 'no-cities') return false;

                    if (rule.required) {
                        return !value || value.toString().trim() === '';
                    }
                    return false;
                })
                .map(field => {
                    // Convert field names to user-friendly names
                    const fieldNames = {
                        name: 'Company Name',
                        uen_number: 'Company UEN Number',
                        contact_number: 'Phone Number',
                        email: 'Email',
                        address: 'Address',
                        country_id: 'Country',
                        state_id: 'State',
                        city_id: 'City',
                        password: 'Password',
                        contact_name: 'Contact Person Name',
                        contact_email: 'Contact Email',
                        contact_phone: 'Contact Phone Number'
                    };
                    return fieldNames[field] || field;
                });

            if (missingFields.length > 0) {
                const errorMessage = `Please complete the following company fields: ${missingFields.join(', ')}`;
                showPopup('error', 'Company Information Incomplete', errorMessage);
                setActiveTab('company'); // Go back to company tab
                return;
            }

            // Check for pattern errors
            const patternErrors = Object.keys(errors).filter(key => validationRules[key]);
            if (patternErrors.length > 0) {
                const errorMessage = 'Some company fields have invalid data. Please check and correct them.';
                showPopup('error', 'Validation Error', errorMessage);
                setActiveTab('company'); // Go back to company tab
                return;
            }
        }

        // Check if "no states" or "no cities" options are selected
        if (companyData.state_id === 'no-states' || companyData.city_id === 'no-cities') {
            showPopup('error', 'Validation Error', 'Selected location does not have states or cities available. Please select a different country.');
            setActiveTab('company');
            return;
        }

        // STEP 2: Validate warehouse form
        if (!validateWarehouseForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required warehouse fields correctly');
            return;
        }

        setLoading(true);
        try {
            // STEP 3: Create company first (if not already created)
            let finalCompanyId = companyId;

            if (!finalCompanyId) {
                const companyResponse = await createCompany({
                    company_name: companyData.name,
                    company_uen_number: companyData.uen_number,
                    email: companyData.email,
                    contact_number: companyData.contact_number,
                    address: companyData.address,
                    country_id: companyData.country_id,
                    state_id: companyData.state_id,
                    city_id: companyData.city_id,
                    password: companyData.password,
                    main_contact_person_name: companyData.contact_name,
                    main_contact_person_email: companyData.contact_email,
                    main_contact_person_phone_number: companyData.contact_phone,
                    remark: companyData.remark,
                    status: 1,
                });

                console.log('Full company response:', companyResponse);
                if (companyResponse.status === 200 || companyResponse.status === 201) {
                    console.log('Company created successfully:', companyResponse.data);

                    finalCompanyId = companyResponse.data.id || companyResponse.data.company_id || companyResponse.data.data?.id;

                    if (finalCompanyId) {
                        setCompanyId(finalCompanyId);
                    } else {
                        console.error('Company ID not found in response:', companyResponse.data);
                        showPopup('error', 'Error', 'Company created but ID not found');
                        setLoading(false);
                        return;
                    }
                } else {
                    showPopup('error', 'Error', 'Unexpected response status: ' + companyResponse.status);
                    setLoading(false);
                    return;
                }
            }

            // STEP 4: Create warehouses
            const apiData = {
                company_id: finalCompanyId,
                company_warehouses: warehouseData.map(warehouse => ({
                    warehouse_id: warehouse.warehouse_id,
                    membership_till: warehouse.membership_till,
                    min_lot: warehouse.lot_min,
                    max_lot: warehouse.lot_max,
                    is_primary: warehouse.is_primary
                }))
            };

            console.log('Submitting warehouse data:', apiData);
            const response = await createCompanyWarehouse(apiData);

            if (response.status === 200 || response.status === 201) {
                showPopup('success', 'Success', 'Company and warehouses created successfully');
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/company");
            } else {
                showPopup('error', 'Error', 'Unexpected response status: ' + response.status);
            }
        } catch (error) {
            console.error('Error creating company/warehouses:', error);
            const errorMessage = error.response?.data?.message || 'Failed to create company and warehouses';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${Styles.company_add}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Create Company</h2>
            </div>

            <div className={`${Styles.company_form}`}>
                <div className={`${Styles.handle_heading}`}>
                    <div className={`${Styles.handle_form}`}>
                        <div className={`${Styles.tab}`}>
                            <h4 onClick={() => handleTabChange('company')} className={`${activeTab === 'company' ? Styles.active : ''}`}>Company details</h4>
                            {activeTab === 'company' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                        <div className={`${Styles.tab}`}>
                            <h4
                                onClick={() => handleTabChange('warehouse')}
                            // className={`${activeTab === 'warehouse' ? Styles.active : ''} ${!companyId ? Styles.disabled : ''}`}
                            // style={{
                            //     cursor: !companyId ? 'not-allowed' : 'pointer',
                            //     opacity: !companyId ? 0.5 : 1
                            // }}
                            >
                                Warehouse details
                            </h4>
                            {activeTab === 'warehouse' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                    </div>
                    <hr />
                </div>

                {activeTab === 'company' && (
                    <div className={`${Styles.company_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Company details</h4>
                            <hr />
                        </div>
                        <div className={`${Styles.form}`}>
                            <div className={`${Styles.label_input} ${errors.name ? Styles.error : ''}`}>
                                <label>Company name<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="name"
                                        value={companyData.name}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company Name"
                                        className={`${errors.name ? Styles.errorInput : ''}`}
                                    />
                                    {errors.name && <span className={`${Styles.errorMessage}`}>{errors.name}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.uen_number ? Styles.error : ''}`}>
                                <label>Company UEN number<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="uen_number"
                                        value={companyData.uen_number}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company UEN number"
                                        className={`${errors.uen_number ? Styles.errorInput : ''}`}
                                    />
                                    {errors.uen_number && <span className={`${Styles.errorMessage}`}>{errors.uen_number}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.contact_number ? Styles.error : ''}`}>
                                <label>Phone number<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="tel"
                                        name="contact_number"
                                        value={companyData.contact_number}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company Phone number"
                                        className={`${errors.contact_number ? Styles.errorInput : ''}`}
                                        maxLength="10"
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                    />
                                    {errors.contact_number && <span className={`${Styles.errorMessage}`}>{errors.contact_number}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.email ? Styles.error : ''}`}>
                                <label>Email<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="email"
                                        value={companyData.email}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company Email"
                                        className={`${errors.email ? Styles.errorInput : ''}`}
                                    />
                                    {errors.email && <span className={`${Styles.errorMessage}`}>{errors.email}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.address ? Styles.error : ''}`}>
                                <label>Address<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="address"
                                        value={companyData.address}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company Address"
                                        className={`${errors.address ? Styles.errorInput : ''}`}
                                    />
                                    {errors.address && <span className={`${Styles.errorMessage}`}>{errors.address}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.country_id || errors.state_id || errors.city_id ? Styles.error : ''}`}>
                                <label>Country /State /City<span>*</span></label>
                                <div className={`${Styles.select_container}`}>
                                    <div className={`${Styles.country}`}>
                                        <select name='country_id' value={companyData.country_id} onChange={handleCompanyInputChange} className={`${errors.country_id ? Styles.errorInput : ''}`} disabled={loadingLocations.countries}>
                                            <option value="" hidden>{loadingLocations.countries ? 'Loading...' : 'Select Country'}</option>
                                            {countries.map(country => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.country_id && <span className={`${Styles.errorMessage}`}>{errors.country_id}</span>}
                                    </div>
                                    <div className={`${Styles.state}`}>
                                        <select name='state_id' value={companyData.state_id} onChange={handleCompanyInputChange} className={`${errors.state_id ? Styles.errorInput : ''}`} disabled={!companyData.country_id || loadingLocations.states}>
                                            <option value="" hidden>{loadingLocations.states ? 'Loading...' : 'Select State'}</option>
                                            {states.map(state => (
                                                <option key={state.id} value={state.id} disabled={state.disabled || false}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.state_id && <span className={`${Styles.errorMessage}`}>{errors.state_id}</span>}
                                    </div>
                                    <div className={`${Styles.city}`}>
                                        <select name='city_id' value={companyData.city_id} onChange={handleCompanyInputChange} className={`${errors.city_id ? Styles.errorInput : ''}`} disabled={!companyData.state_id || loadingLocations.cities || companyData.state_id === 'no-states'}>
                                            <option value="" hidden>{loadingLocations.cities ? 'Loading...' : companyData.state_id === 'no-states' ? 'No cities available' : 'Select City'}</option>
                                            {cities.map(city => (
                                                <option key={city.id} value={city.id} disabled={city.disabled || false}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.city_id && <span className={`${Styles.errorMessage}`}>{errors.city_id}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.password ? Styles.error : ''}`}>
                                <label>Password<span>*</span></label>
                                <div className={`${Styles.password_field}`}>
                                    <input
                                        name="password"
                                        type={passwordVisible ? "text" : "password"}
                                        value={companyData.password}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company Password"
                                    />
                                    <img
                                        src={passwordVisible ? OpenEye : CloseEye}
                                        onClick={() => setPasswordVisible(!passwordVisible)}
                                        alt="Toggle visibility"
                                        className={`${Styles.eye_icon}`}
                                    />
                                    {errors.password && <span className={`${Styles.errorMessage}`}>{errors.password}</span>}
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.contact_person}`}>
                            <div className={`${Styles.title2}`}>
                                <h1>Main contact person</h1>
                                <hr />
                            </div>
                            <div className={`${Styles.form}`}>
                                <div className={`${Styles.label_input} ${errors.contact_name ? Styles.error : ''}`}>
                                    <label>Contact person name<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="text"
                                            name="contact_name"
                                            value={companyData.contact_name}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Contact person name"
                                            className={`${errors.contact_name ? Styles.errorInput : ''}`}
                                        />
                                        {errors.contact_name && <span className={`${Styles.errorMessage}`}>{errors.contact_name}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.contact_email ? Styles.error : ''}`}>
                                    <label>Email<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="text"
                                            name="contact_email"
                                            value={companyData.contact_email}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Contact person email"
                                            className={`${errors.contact_email ? Styles.errorInput : ''}`}
                                        />
                                        {errors.contact_email && <span className={`${Styles.errorMessage}`}>{errors.contact_email}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.contact_phone ? Styles.error : ''}`}>
                                    <label>Phone number<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="tel"
                                            name="contact_phone"
                                            value={companyData.contact_phone}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Contact person phone number"
                                            className={`${errors.contact_phone ? Styles.errorInput : ''}`}
                                            maxLength="10"
                                            pattern="[0-9]*"
                                            inputMode="numeric"
                                        />
                                        {errors.contact_phone && <span className={`${Styles.errorMessage}`}>{errors.contact_phone}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.remark ? Styles.error : ''}`}>
                                    <label>Remark</label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="text"
                                            name="remark"
                                            value={companyData.remark}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Company Remark"
                                            className={`${errors.remark ? Styles.errorInput : ''}`}
                                        />
                                        {errors.remark && <span className={`${Styles.errorMessage}`}>{errors.remark}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleNext} disabled={loading}>{loading ? <AiOutlineLoading /> : "Next"}</button>
                            <button onClick={() => navigate("/company")} disabled={loading}>Cancel</button>
                        </div>
                    </div>
                )}

                {activeTab === 'warehouse' && (
                    <div className={`${Styles.warehouse_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Warehouse details</h4>
                            <hr />
                        </div>

                        {/* Warehouse Cards Display */}
                        <div className={`${Styles.warehouse_cards}`}>
                            {warehouseData.map((warehouse, index) => {
                                const selectedWarehouse = warehouses.find(w => w.id == warehouse.warehouse_id);
                                return (
                                    <div
                                        key={index}
                                        className={`${Styles.warehouse_card} ${warehouse.is_primary ? Styles.selected : ''}`}
                                        onClick={() => handlePrimaryChange(index)}
                                    >
                                        <div className={`${Styles.card_content}`}>
                                            <div className={`${Styles.location}`}>
                                                {selectedWarehouse ? selectedWarehouse.name : 'Select Warehouse'} (warehouse name)
                                            </div>
                                            <div className={`${Styles.lot}`}>
                                                Lot # {warehouse.lot_min && warehouse.lot_max ? `${warehouse.lot_min}-${warehouse.lot_max}` : 'Not set'}
                                            </div>
                                            <div className={`${Styles.membership}`}>
                                                Membership till {warehouse.membership_till || 'Not set'}
                                            </div>
                                            <div className={`${Styles.actions}`}>
                                                <span className={`${Styles.set_default}`}>
                                                    {warehouse.is_primary ? 'Primary Warehouse' : 'Set as primary'}
                                                </span>
                                                {warehouseData.length > 1 && (
                                                    <span
                                                        className={`${Styles.remove}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeWarehouse(index);
                                                        }}
                                                    >
                                                        Remove
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {warehouse.is_primary && (
                                            <div className={`${Styles.check_icon}`}>
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                                    <circle cx="12" cy="12" r="10" stroke="#8148f5" strokeWidth="2" fill="white" />
                                                    <path d="M9 12l2 2l4 -4" stroke="#8148f5" strokeWidth="2" fill="none" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Add New Warehouse Button */}
                        <div className={`${Styles.add_warehouse_btn}`}>
                            <button
                                type="button"
                                onClick={addNewWarehouse}
                                className={`${Styles.add_btn}`}
                            >
                                + Add Another Warehouse
                            </button>
                        </div>

                        {/* Warehouse Forms */}
                        {warehouseData.map((warehouse, index) => (
                            <div key={index} className={`${Styles.form} ${Styles.warehouse_form}`}>
                                <h5>Warehouse {index + 1}</h5>

                                <div className={`${Styles.label_input} ${errors[`membership_till_${index}`] ? Styles.error : ''}`}>
                                    <label>Membership till<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="date"
                                            name="membership_till"
                                            value={warehouse.membership_till}
                                            onChange={(e) => handleWarehouseInputChange(e, index)}
                                            placeholder="Enter membership till"
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                        {errors[`membership_till_${index}`] &&
                                            <span className={`${Styles.errorMessage}`}>{errors[`membership_till_${index}`]}</span>
                                        }
                                    </div>
                                </div>

                                <div className={`${Styles.label_input} ${errors[`warehouse_id_${index}`] ? Styles.error : ''}`}>
                                    <label>Warehouse Name<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <select
                                            name="warehouse_id"
                                            required
                                            className={`${errors[`warehouse_id_${index}`] ? Styles.errorInput : ''}`}
                                            value={warehouse.warehouse_id}
                                            onChange={(e) => handleWarehouseInputChange(e, index)}
                                        >
                                            <option value="" hidden>Select Warehouse Name</option>
                                            {warehouses.filter(w => w.status === 1).map(w => (
                                                <option key={w.id} value={w.id}>
                                                    {w.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`warehouse_id_${index}`] &&
                                            <span className={`${Styles.errorMessage}`}>{errors[`warehouse_id_${index}`]}</span>
                                        }
                                    </div>
                                </div>

                                <div className={`${Styles.label_input} ${errors[`lot_min_${index}`] ? Styles.error : ''}`}>
                                    <label>Lot#<span>*</span></label>
                                    <div className={`${Styles.lot}`}>
                                        <div className={`${Styles.lot_field} ${errors[`lot_min_${index}`] ? Styles.errorInput : ''}`}>
                                            <input
                                                className={`${Styles.lot_field} ${errors[`lot_min_${index}`] ? Styles.errorInput : ''}`}
                                                type="text"
                                                inputMode="numeric"
                                                name="lot_min"
                                                placeholder="Minimum"
                                                value="25"
                                                readOnly
                                                disabled
                                                onChange={(e) => handleWarehouseNumberInput(index, 'lot_min', e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            {errors[`lot_min_${index}`] &&
                                                <span className={`${Styles.errorMessage}`}>{errors[`lot_min_${index}`]}</span>
                                            }
                                            <div className={`${Styles.warehouse_note} ${Styles.disabled_note}`}>
                                                <span className={Styles.note_icon_disabled}>ⓘ</span>
                                                <span className={Styles.note_text_disabled}>Minimum lot must be 25</span>
                                            </div>
                                        </div>
                                        <div className={`${Styles.lot_field} ${errors[`lot_max_${index}`] ? Styles.errorInput : ''}`}>
                                            <input
                                                className={`${Styles.lot_field} ${errors[`lot_max_${index}`] ? Styles.errorInput : ''}`}
                                                type="text"
                                                inputMode="numeric"
                                                name="lot_max"
                                                placeholder="Maximum"
                                                value={warehouse.lot_max}
                                                onChange={(e) => handleWarehouseNumberInput(index, 'lot_max', e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            {errors[`lot_max_${index}`] &&
                                                <span className={`${Styles.errorMessage}`}>{errors[`lot_max_${index}`]}</span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleSubmitWarehouse} disabled={loading}>
                                {loading ? <AiOutlineLoading /> : "Create"}
                            </button>
                            <button onClick={() => navigate("/company")}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CompanyAdd
